import { useState } from 'react'
import { useTheme } from 'next-themes'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Checkbox } from '../components/ui/checkbox'
import { Switch } from '../components/ui/switch'
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Separator } from '../components/ui/separator'
import { ScrollArea } from '../components/ui/scroll-area'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog'
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet'
import { Drawer, DrawerContent, DrawerTrigger, DrawerHeader, DrawerTitle } from '../components/ui/drawer'
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '../components/ui/avatar'
import { Badge } from '../components/ui/badge'
import { Skeleton } from '../components/ui/skeleton'
import { Kbd } from '../components/ui/kbd'
import { Pagination, usePaginatedSlice } from '../components/ui/pagination'

// Outline-button styling applied directly to a Radix trigger. The overlay
// triggers previously used `asChild` with the custom <Button>, which is a plain
// (non-forwardRef) function component — Radix could not attach the trigger ref,
// so anchor-positioned overlays (popover / dropdown / tooltip) failed to open.
// Letting the Trigger render its own <button> restores the ref + positioning.
const overlayBtn =
  'inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm font-medium text-foreground shadow-xs transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-12">
      <h2 className="text-xl font-semibold mb-4 text-foreground">{title}</h2>
      <div className="flex flex-wrap gap-4 items-start">{children}</div>
    </section>
  )
}

function PaginationDemo() {
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(10)
  const items = Array.from({ length: 42 }, (_, i) => i + 1)
  const shown = usePaginatedSlice(items, page, size)
  return (
    <div className="w-full max-w-xl overflow-hidden rounded-lg border border-border">
      <div className="px-4 py-3 text-sm text-muted-foreground">Page items: {shown.join(', ')}</div>
      <Pagination
        total={items.length}
        page={page}
        pageSize={size}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setSize(s); setPage(1) }}
      />
    </div>
  )
}

export default function UIShowcase() {
  const { theme, setTheme } = useTheme()

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background text-foreground p-8">
        <header className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Metronic UI Showcase</h1>
            <p className="text-muted-foreground">Phase 0 — visual verification of 22 of 25 primitives in light/dark modes</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setTheme('light')} selected={theme === 'light'}>Light</Button>
            <Button variant="outline" onClick={() => setTheme('dark')} selected={theme === 'dark'}>Dark</Button>
          </div>
        </header>

        <Section title="Buttons">
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Destructive</Button>
          <Button variant="dashed">Dashed</Button>
          <Button variant="primary" size="sm">Small</Button>
          <Button variant="primary" size="lg">Large</Button>
          <Button variant="primary" disabled>Disabled</Button>
        </Section>

        <Section title="Form inputs">
          <div className="flex flex-col gap-2 w-64">
            <Label htmlFor="email">Email</Label>
            <Input id="email" placeholder="you@example.com" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="ck" /><Label htmlFor="ck">Checkbox</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="sw" /><Label htmlFor="sw">Switch</Label>
          </div>
          <RadioGroup defaultValue="a">
            <div className="flex items-center gap-2"><RadioGroupItem value="a" id="r1" /><Label htmlFor="r1">Option A</Label></div>
            <div className="flex items-center gap-2"><RadioGroupItem value="b" id="r2" /><Label htmlFor="r2">Option B</Label></div>
          </RadioGroup>
          <Select>
            <SelectTrigger className="w-48"><SelectValue placeholder="Pick" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="a">Option A</SelectItem>
              <SelectItem value="b">Option B</SelectItem>
              <SelectItem value="c">Option C</SelectItem>
            </SelectContent>
          </Select>
        </Section>

        <Section title="Cards & layout">
          <Card className="w-72">
            <CardHeader><CardTitle>Card title</CardTitle></CardHeader>
            <CardContent>Card body content with some text.</CardContent>
          </Card>
          <div className="w-32"><Separator /><span className="text-xs text-muted-foreground">separator above</span></div>
          <ScrollArea className="h-32 w-48 border border-border rounded-md p-3">
            {Array.from({ length: 20 }).map((_, i) => <div key={i}>Row {i + 1}</div>)}
          </ScrollArea>
        </Section>

        <Section title="Accordion & Tabs">
          <Accordion type="single" collapsible className="w-72">
            <AccordionItem value="a"><AccordionTrigger>Section A</AccordionTrigger><AccordionContent>Body A</AccordionContent></AccordionItem>
            <AccordionItem value="b"><AccordionTrigger>Section B</AccordionTrigger><AccordionContent>Body B</AccordionContent></AccordionItem>
          </Accordion>
          <Tabs defaultValue="t1" className="w-72">
            <TabsList>
              <TabsTrigger value="t1">Tab 1</TabsTrigger>
              <TabsTrigger value="t2">Tab 2</TabsTrigger>
            </TabsList>
            <TabsContent value="t1">Content of tab 1</TabsContent>
            <TabsContent value="t2">Content of tab 2</TabsContent>
          </Tabs>
        </Section>

        <Section title="Overlays">
          <Dialog>
            <DialogTrigger className={overlayBtn}>Open dialog</DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Dialog</DialogTitle><DialogDescription>This is a dialog.</DialogDescription></DialogHeader>
            </DialogContent>
          </Dialog>
          <Sheet>
            <SheetTrigger className={overlayBtn}>Open sheet</SheetTrigger>
            <SheetContent><SheetHeader><SheetTitle>Sheet</SheetTitle></SheetHeader></SheetContent>
          </Sheet>
          <Drawer>
            <DrawerTrigger className={overlayBtn}>Open drawer</DrawerTrigger>
            <DrawerContent><DrawerHeader><DrawerTitle>Drawer</DrawerTitle></DrawerHeader></DrawerContent>
          </Drawer>
          <Popover>
            <PopoverTrigger className={overlayBtn}>Popover</PopoverTrigger>
            <PopoverContent>Popover content</PopoverContent>
          </Popover>
          <Tooltip>
            <TooltipTrigger className={overlayBtn}>Hover for tip</TooltipTrigger>
            <TooltipContent>Tooltip!</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger className={overlayBtn}>Dropdown</DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>Item 1</DropdownMenuItem>
              <DropdownMenuItem>Item 2</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Section>

        <Section title="Pagination">
          <PaginationDemo />
        </Section>

        <Section title="Display">
          <Avatar><AvatarFallback>DC</AvatarFallback></Avatar>
          <Badge>Default</Badge>
          <Badge variant="outline">Outline</Badge>
          <Badge variant="destructive">Destructive</Badge>
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="flex items-center gap-1">Press <Kbd>Ctrl</Kbd>+<Kbd>K</Kbd></div>
        </Section>
      </div>
    </TooltipProvider>
  )
}
